'use client'

import { Button } from '@/core/components/form/button/button'
import { LuX } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import CommentComposer from '@/features/veluvit/components/comments/comment-composer'
import CommentList from '@/features/veluvit/components/comments/comment-list'
import { useWorkComments } from '@/features/veluvit/components/comments/use-work-comments'
import { SheetHead } from '@/features/veluvit/components/overlay/layer-parts'
import { fmt } from '@/features/veluvit/lib/format'

const CLOSED_TEXT = '작가가 이 작품의 댓글을 닫아 두었어요.'

/** 바텀시트 판을 유리 대신 불투명 면으로 — 사진 위에서 글이 비쳐 읽기 어려웠다(2026-10-08). 시트 열 때 className 으로 넘긴다 */
export const COMMENTS_SHEET_CLASS = 'bg-surface! backdrop-blur-none!'

function Count({ c }: { c: ReturnType<typeof useWorkComments> }) {
  return c.items ? (
    <span className="ml-1.5 text-c14 font-semibold text-t3 tabular-nums">
      {fmt(c.total)}
    </span>
  ) : null
}

/**
 * 탐색(쇼츠) 댓글 — 유튜브 쇼츠처럼 PC 는 카드 오른쪽에 붙는 패널(CommentsPanel), 모바일은 바텀시트(CommentsSheet).
 * 머리(댓글 n · 닫기) → 목록(스크롤) → 맨 아래 쓰기 한 줄(누르면 취소 · 등록). 답글은 각 댓글 아래에서 연다.
 * 판은 유리가 아니라 불투명 면 — 사진 위에 떠서 글이 비쳤다.
 */
export default function CommentsPanel({
  workId,
  onClose,
  className,
}: {
  workId: string
  onClose: () => void
  className?: string
}) {
  const c = useWorkComments(workId)
  return (
    <aside
      aria-label="댓글"
      className={cn(
        'flex min-h-0 flex-col overflow-hidden rounded-[20px] bg-surface shadow-[0_24px_60px_-20px_rgb(0_0_0/0.8),inset_0_0_0_1px_rgb(255_255_255/0.08)]',
        className
      )}
    >
      <header className="flex flex-none items-center gap-2 border-b border-foreground/[0.08] px-5 py-3.5">
        <h2 className="text-c17 font-bold">
          댓글
          <Count c={c} />
        </h2>
        <Button
          type="button"
          aria-label="댓글 닫기"
          onClick={onClose}
          className="ml-auto grid size-9 cursor-pointer place-items-center rounded-full text-t2 transition-colors hover:bg-foreground/[0.08] hover:text-t1 focus-visible:bg-foreground/[0.08]"
          variant="bare"
        >
          <LuX aria-hidden="true" className="size-5" />
        </Button>
      </header>
      {c.closed ? (
        <p className="grid flex-1 place-items-center px-6 text-center text-c13 text-t3">
          {CLOSED_TEXT}
        </p>
      ) : (
        <>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4 scrollbar-none">
            <CommentList c={c} />
          </div>
          <CommentComposer
            c={c}
            className="flex-none border-t border-foreground/[0.08] px-5 py-3"
          />
        </>
      )}
    </aside>
  )
}

/**
 * 모바일 바텀시트 판 — 쓰기 한 줄은 시트 맨 아래에 붙여 둔다(목록을 내려도 늘 보인다, 쇼츠와 같은 자리).
 * 시트 본문 칸이 스크롤 칸이라, 목록이 짧아도 쓰기 줄이 바닥에 붙게 높이를 채우고(min-h-full) 쓰기 줄은 sticky 로 둔다.
 */
export function CommentsSheet({ workId }: { workId: string }) {
  const c = useWorkComments(workId)
  return (
    <>
      <SheetHead
        title={
          <>
            댓글
            <Count c={c} />
          </>
        }
      />
      {c.closed ? (
        <p className="py-10 text-center text-c13 text-t3">{CLOSED_TEXT}</p>
      ) : (
        <div className="flex min-h-full flex-col">
          <CommentList c={c} className="flex-1 pt-1 pb-4" />
          <CommentComposer
            c={c}
            className="sticky bottom-0 -mx-(--layer-px) -mb-(--layer-pb) border-t border-foreground/[0.08] bg-surface px-(--layer-px) pt-3 pb-[max(12px,env(safe-area-inset-bottom))]"
          />
        </div>
      )}
    </>
  )
}
